import { useState, useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme-context";
import { Clock, Flame, Sparkles, Tag } from "lucide-react";

interface ProductBadgesProps {
  product: {
    salePrice?: number | null;
    price: number;
    quantity?: number | null;
    createdAt?: string;
    featured?: boolean;
  };
  size?: "sm" | "md";
  showCountdown?: boolean;
  className?: string;
}

export function ProductBadges({ product, size = "sm", showCountdown = false, className = "" }: ProductBadgesProps) {
  const { lang } = useI18n();
  const theme = useTheme();
  const badgeShape = (theme?.badgeShape as string) || "rounded";
  const shapeClass =
    badgeShape === "square" ? "rounded-none"
    : badgeShape === "circle" ? "rounded-full px-3"
    : badgeShape === "ribbon" ? "rounded-none rounded-e-full ps-3 pe-4"
    : "rounded";
  const badges: Array<{ label: string; color: string; icon?: React.ReactNode }> = [];

  const isNew = product.createdAt && (Date.now() - new Date(product.createdAt).getTime()) < 14 * 24 * 60 * 60 * 1000;
  const isOnSale = !!product.salePrice && product.salePrice < product.price;
  const isOutOfStock = (product.quantity ?? 0) <= 0;
  const isLowStock = !isOutOfStock && (product.quantity ?? 0) > 0 && (product.quantity ?? 0) <= 5;

  if (isOutOfStock) {
    badges.push({
      label: lang === "ar" ? "نفذت الكمية" : "Sold Out",
      color: "bg-gray-800 text-white",
    });
  }

  if (isOnSale) {
    const discount = Math.round((1 - (product.salePrice! / product.price)) * 100);
    badges.push({
      label: `${lang === "ar" ? "خصم" : "SALE"} ${discount}%`,
      color: "bg-red-500 text-white",
      icon: <Tag className={size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} />,
    });
  }

  if (isNew && !isOutOfStock) {
    badges.push({
      label: lang === "ar" ? "جديد" : "NEW",
      color: "bg-emerald-500 text-white",
      icon: <Sparkles className={size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} />,
    });
  }

  if (product.featured && !isOutOfStock) {
    badges.push({
      label: lang === "ar" ? "مميز" : "HOT",
      color: "bg-amber-500 text-white",
      icon: <Flame className={size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} />,
    });
  }

  if (isLowStock) {
    badges.push({
      label: lang === "ar" ? `آخر ${product.quantity} قطع` : `Only ${product.quantity} left`,
      color: "bg-orange-500 text-white",
    });
  }

  if (badges.length === 0 && !showCountdown) return null;

  const textSize = size === "sm" ? "text-[9px]" : "text-[11px]";
  const paddings = size === "sm" ? "px-1.5 py-0.5" : "px-2.5 py-1";

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {badges.map((badge, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 ${badge.color} ${textSize} ${paddings} ${shapeClass} font-bold tracking-wider uppercase leading-none w-fit`}
        >
          {badge.icon}
          {badge.label}
        </span>
      ))}
      {showCountdown && isOnSale && <CountdownTimer lang={lang} size={size} />}
    </div>
  );
}

function CountdownTimer({ lang, size }: { lang: string; size: string }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  useEffect(() => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + ((7 - endDate.getDay()) % 7 || 7));
    endDate.setHours(23, 59, 59, 999);

    const update = () => {
      const diff = endDate.getTime() - Date.now();
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const textSz = size === "sm" ? "text-[8px]" : "text-[10px]";
  const numSz = size === "sm" ? "text-[10px]" : "text-xs";

  return (
    <div className="flex items-center gap-1 bg-red-50 border border-red-100 rounded px-2 py-1 w-fit">
      <Clock className={`${size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} text-red-500`} />
      <div className="flex items-center gap-0.5">
        {[
          { val: timeLeft.days, label: lang === "ar" ? "ي" : "d" },
          { val: timeLeft.hours, label: lang === "ar" ? "س" : "h" },
          { val: timeLeft.mins, label: lang === "ar" ? "د" : "m" },
          { val: timeLeft.secs, label: lang === "ar" ? "ث" : "s" },
        ].map((t, i) => (
          <span key={i} className={`${numSz} font-mono font-bold text-red-600`}>
            {String(t.val).padStart(2, "0")}
            <span className={`${textSz} text-red-400`}>{t.label}</span>
            {i < 3 && <span className="text-red-300 mx-0.5">:</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
